import type { ComponentType, ReactNode } from 'react';
import {
    calculateOverviewData,
    calculateTikTokData,
    formatGrowth,
    formatVolume,
} from 'utils/helpers';
import { getSeriesColors } from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import { shapeTimeSeries } from 'utils/hooks/sourceDataShaping';
import {
    makeEntityListTooltip,
    type EntityListTooltipProps,
} from './EntityListTooltip';
import { buildBreadcrumb } from '../lib/breadcrumbFormat';
import { growthTone } from '../lib/metricTone';
import { BREAKOUT_DISPLAY } from '../lib/parseMetrics';
import { parentName, pinnedMembers } from '../lib/tableHelpers';
import type {
    AggregateMetrics,
    EntityTimeSeriesRow,
    MetricTileModel,
    RawRow,
    VisualProps,
} from '../types';

export const CHART_HEIGHT = 200;
// The watchlist's 3px reads too heavy at report-tile size.
export const STROKE_WIDTH = '2px';

// The chart's default wrapper paints the prediction zone; this one doesn't.
export const PlainChartWrapper = ({ children }: { children?: ReactNode }) => (
    <div className="flex flex-col">{children}</div>
);

export interface EntityListSeries {
    id: string;
    name: string;
    color: string;
    /** Header-first positional matrix (shapeTimeSeries). */
    timeSeries: unknown[];
    /** The member's raw snapshot row (per-member series only) — the tile
     * recompute's input. */
    row?: RawRow;
}

const growthDisplay = (value: number | null | undefined): string | null => {
    if (value == null) return null;
    const formatted = formatGrowth(value);
    // formatGrowth returns a <Breakout/> element (not a string) for >=1000%.
    return typeof formatted === 'string' ? formatted : BREAKOUT_DISPLAY;
};

export interface ListChartSpec {
    typeLabel: string;
    noun: string;
    nameOf: (row: RawRow) => string | null | undefined;
    idOf: (row: RawRow) => string | null | undefined;
    tiles: (
        aggregate: AggregateMetrics | null | undefined,
    ) => MetricTileModel[];
    aggregateRows?: (rows: RawRow[]) => AggregateMetrics | null;
    /** Names render exactly as stored — a hashtag's casing IS the hashtag. */
    preserveNameCase?: boolean;
    /** Must be built once at module scope: the chart takes a component, and a
     * new one each render remounts the tooltip. */
    Tooltip: ComponentType<EntityListTooltipProps>;
}

export function buildListChrome({
    input,
    output,
    spec,
    customTitle,
}: VisualProps & { spec: ListChartSpec; customTitle?: string }) {
    // The handler attaches `_rows` only when it shaped at least one row, so a
    // pinned list where no member has data arrives without it — an empty
    // result, not drift (the tables show "No results" for the same payload).
    const rows = output?._rows ?? [];
    if (!Array.isArray(rows)) {
        throw new Error(
            'entity list chart: expected a `_rows` array on the ' +
                'get_entity_data output',
        );
    }
    return {
        rows,
        breadcrumb: buildBreadcrumb(output, spec.typeLabel),
        title: {
            custom: customTitle || undefined,
            parent: parentName(output),
            noun: spec.noun,
            // From `_rows`, not `result.metrics`: under `aggregate` the metrics
            // lead with the synthetic Combined row.
            ...(output?.result?.pinned_count
                ? pinnedMembers(rows.map(spec.nameOf))
                : {}),
            preserveMemberCase: spec.preserveNameCase,
            lifeCycle: input?.life_cycle,
        },
        link: output?.result?.dashboard_url,
    };
}

/** Members without a usable series are dropped, so the result does NOT align
 * index-wise with `rows`. */
export function buildListSeries(
    rows: RawRow[],
    spec: ListChartSpec,
): EntityListSeries[] {
    return rows
        .map((row) => {
            const name = spec.nameOf(row);
            if (!name) {
                throw new Error(
                    'entity list time series: row is missing its name',
                );
            }
            return {
                row,
                name,
                shaped: shapeTimeSeries(
                    (row.time_series ?? []) as EntityTimeSeriesRow[],
                ),
            };
        })
        .filter(({ shaped }) => shaped)
        .map(({ row, name, shaped }, i) => ({
            id: String(spec.idOf(row) ?? name),
            name,
            color: getSeriesColors(i),
            timeSeries: shaped as unknown[],
            row,
        }));
}

/** The group snapshot over a member subset: the same math as the backend's
 * aggregate_snapshot. `calculateOverviewData` is untyped JS; this is its one
 * typed boundary. */
const aggregateRows = (rows: RawRow[]): AggregateMetrics | null =>
    calculateOverviewData(rows) as AggregateMetrics | null;

/** Must stay numerically identical to the backend's aggregate_tiktok_snapshot. */
const aggregateTikTokRows = (rows: RawRow[]): AggregateMetrics | null =>
    calculateTikTokData(rows) as AggregateMetrics | null;

const volumeTile = (
    label: string,
    value: number | null | undefined,
): MetricTileModel => ({
    label,
    value: value == null ? null : formatVolume(value),
});

const growthTile = (
    label: string,
    value: number | null | undefined,
): MetricTileModel => {
    const display = growthDisplay(value);
    return { label, value: display, tone: growthTone(display) };
};

function buildAggregateTiles(
    aggregate: AggregateMetrics | null | undefined,
): MetricTileModel[] {
    return [
        volumeTile('Popularity', aggregate?.popularity_index_avg_12_month),
        growthTile('YoY', aggregate?.YoY),
        {
            label: 'Predicted YoY',
            value: growthDisplay(aggregate?.predicted_YoY),
        },
    ];
}

/** TikTok publishes no forecast, so the third tile is the long-term rate, not
 * a predicted one. */
function buildHashtagAggregateTiles(
    aggregate: AggregateMetrics | null | undefined,
): MetricTileModel[] {
    return [
        volumeTile('Avg Weekly Views', aggregate?.non_cum_avg_weekly_views),
        growthTile('WoW', aggregate?.non_cum_WoW),
        growthTile('YoY', aggregate?.non_cum_YoY_rolling),
    ];
}

const POP_INDEX_TOOLTIP = makeEntityListTooltip({
    valueLabel: 'Popularity',
    predictedValueLabel: 'Predicted Popularity',
});
const HASHTAG_TOOLTIP = makeEntityListTooltip({
    valueLabel: 'Views',
    preserveNameCase: true,
});

const brandListSpec: ListChartSpec = {
    typeLabel: 'Brand',
    noun: 'brands',
    nameOf: (row) => row.brand_name ?? row.category_name,
    idOf: (row) => (row.brand ?? row.category) as string | undefined,
    tiles: buildAggregateTiles,
    aggregateRows,
    Tooltip: POP_INDEX_TOOLTIP,
};

const categoryListSpec: ListChartSpec = {
    ...brandListSpec,
    typeLabel: 'Category',
    noun: 'categories',
};

/** Returns a module constant, never a fresh object: the chart views memoize on
 * the spec. */
export const entityListSpec = (input: VisualProps['input']): ListChartSpec =>
    input?.entity_relationship === 'categories'
        ? categoryListSpec
        : brandListSpec;

export const hashtagListSpec: ListChartSpec = {
    typeLabel: 'Hashtag',
    noun: 'hashtags',
    nameOf: (row) => row.hashtag_name as string | undefined,
    idOf: (row) => row.hashtag as string | undefined,
    tiles: buildHashtagAggregateTiles,
    aggregateRows: aggregateTikTokRows,
    preserveNameCase: true,
    Tooltip: HASHTAG_TOOLTIP,
};
